import image from "../../Images/ServerlessApi.png"
import "../ContainerWebApp/ContainerWebApp.css"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark} from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const ServerlessApi = () => {
    const [activeSection, setActiveSection] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
            const handleScroll = () => {
                const sections = document.querySelectorAll("[data-section]");
                let current = "";
    
                sections.forEach((section) => {
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.clientHeight;
                    if (window.pageYOffset >= sectionTop - 100) {
                        current = section.getAttribute("data-section");
                    }
                });
    
                setActiveSection(current);
            };
    
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }, []);
    
        const scrollToSection = (sectionId) => {
            const element = document.querySelector(`[data-section="${sectionId}"]`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

    return (
        <div className="documentation-layout">
            <aside className="sidebar">
                <nav className="sidebar-nav">
                    <button className="back-button" onClick={() => navigate('/')}>
                        ← Back
                    </button>
                    <h3 className="sidebar-title">Contents</h3>
                    <ul className="sidebar-list">

                        <li>
                            <a 
                                className={activeSection === "architecture" ? "active" : ""}
                                onClick={() => scrollToSection("architecture")}
                            >
                                Architecture
                            </a>
                        </li>
                            <a 
                                className={activeSection === "details" ? "active" : ""}
                                onClick={() => scrollToSection("details")}
                            >
                                Details
                            </a>
                        <li>
                            <a 
                                className={activeSection === "instantiation-code" ? "active" : ""}
                                onClick={() => scrollToSection("instantiation-code")}
                            >
                                Instantiation
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "stack-code" ? "active" : ""}
                                onClick={() => scrollToSection("stack-code")}
                            >
                                Stack Code
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "implementation-details" ? "active" : ""}
                                onClick={() => scrollToSection("implementation-details")}
                            >
                                Implementation Details
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>
            <main className="documentation-page">
                <div className="title-container" data-section="title">
                    <ReactMarkdown>
                        {`# Serverless API Stack`}
                    </ReactMarkdown>
                </div>
                <div className="image-container" data-section="architecture">
                    <img className="image" src={image} />
                </div>
                <div className="markdown-left" data-section="details">
                    <ReactMarkdown>
                        {`
## Architecture
- **API Gateway**: Managed service that handles REST API requests and routes them to backend services
- **Lambda Functions**: Serverless compute functions that execute business logic on demand
- **DynamoDB**: Fully managed NoSQL database service for fast, scalable data storage

## Use Cases
- RESTful APIs and microservices architectures
- Serverless web and mobile application backends
- Event-driven applications with variable traffic patterns
- Rapid prototyping and development with minimal infrastructure management
- Applications requiring automatic scaling from zero to thousands of requests

## Cost
- **API Gateway**: $3.50 per million requests
- **Lambda**: $0.20 per 1M requests
- **DynamoDB**: On-demand pricing at $1.25 per million write requests, $0.25 per million read requests + storage at $0.25/GB/month

*Note: Serverless pricing scales with actual usage - you only pay for what you use*
                        `}
                    </ReactMarkdown>
                </div>
 <div className="code-title-container" data-section="instantiation-code">
                    <ReactMarkdown>
                        {`# app.ts`}
                    </ReactMarkdown>
                </div>

                    <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
#!/usr/bin/env node
import 'source-map-support/register';
import * as cdk from 'aws-cdk-lib/core';
import { ServerlessApiStack } from '../lib/serverless_api_stack';

const app = new cdk.App();

new ServerlessApiStack(app, "DevItemsApi", {
  environment: 'dev',
  enableCors: true,
  env: {
    account: process.env.CDK_DEFAULT_ACCOUNT,
    region: process.env.CDK_DEFAULT_REGION,
  }
})
`}
                    </SyntaxHighlighter>

                </div>
                <div className="code-title-container" data-section="stack-code">
                    <ReactMarkdown>
                        {`# serverless-api-stack.ts`}
                    </ReactMarkdown>
                </div>

                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
                        {`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as dynamodb from "aws-cdk-lib/aws-dynamodb";
import * as lambda from "aws-cdk-lib/aws-lambda";
import * as apigateway from "aws-cdk-lib/aws-apigateway";


interface ServerlessApiStackProps extends cdk.StackProps {
  environment?: "dev" | "prod";
  tableName?: string;
  enableAuth?: boolean;
  enableCors?: boolean;
  readCapacity?: number;
  writeCapacity?: number;
}

export class ServerlessApiStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props?: ServerlessApiStackProps) {
    super(scope, id, props);

    const environment = props?.environment || "dev";

    const table = new dynamodb.Table(this, "ItemsTable", {
      partitionKey: {name: 'id', type: dynamodb.AttributeType.STRING},
      tableName: props?.tableName,
      billingMode: props?.environment === "prod" ? dynamodb.BillingMode.PROVISIONED : dynamodb.BillingMode.PAY_PER_REQUEST,
      readCapacity: props?.environment === "prod" ? (props.readCapacity || 5) : undefined,
      writeCapacity: props?.environment === 'prod' ? (props.writeCapacity || 5) : undefined,
      removalPolicy: cdk.RemovalPolicy.DESTROY,
    })

    const createItemFunction = new lambda.Function(this, "CreateItemFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset('lambdas/create-item'),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const getItemsFunction = new lambda.Function(this, "GetItemsFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset("lambdas/get-items"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const updateItemFunction = new lambda.Function(this, "UpdateItemFunctions", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset("lambdas/update-item"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const deleteItemFunction = new lambda.Function(this, "DeleteItemFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.js",
      code: lambda.Code.fromAsset("lambdas/delete-item"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const getItemFunction = new lambda.Function(this, "GetItemFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.js",
      code: lambda.Code.fromAsset("lambdas/get-item"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    table.grantReadWriteData(createItemFunction);
    table.grantReadData(getItemsFunction)
    table.grantReadWriteData(updateItemFunction);
    table.grantWriteData(deleteItemFunction);
    table.grantReadData(getItemFunction);

    const api = new apigateway.RestApi(this, "ItemsApi", {
      restApiName: "Items service",
      description: "API for managing items",
      defaultCorsPreflightOptions: props?.enableCors ? {
        allowOrigins: apigateway.Cors.ALL_ORIGINS,
        allowMethods: apigateway.Cors.ALL_METHODS,
      } : undefined,
    })

    const items = api.root.addResource("items");

    items.addMethod("POST", new apigateway.LambdaIntegration(createItemFunction));
    items.addMethod("GET", new apigateway.LambdaIntegration(getItemsFunction));
    

    const singleItem = items.addResource("{id}");

    singleItem.addMethod("GET", new apigateway.LambdaIntegration(getItemFunction));
    singleItem.addMethod("PUT", new apigateway.LambdaIntegration(updateItemFunction));
    singleItem.addMethod("DELETE", new apigateway.LambdaIntegration(deleteItemFunction));

    new cdk.CfnOutput(this, "ApiEndpoint", {
      value: api.url,
      description: "API Gateway endpoint URL",
    })

    new cdk.CfnOutput(this, "TableName", {
      value: table.tableName,
      description: "DynamoDB table name",
    })

    new cdk.CfnOutput(this, "ExampleCurlCOmmand", {
      value: ${`curl -X POST ${"${api.url}"}items -H "Content-type: application/json" -d '{"id":"123", "name": "Test Item"}'`},
      description: "Example command to create an item",
    })

  }
}
`}
                    </SyntaxHighlighter>
                </div>
<div className="code-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Implementation Details`}
                    </ReactMarkdown>
                </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Import Statements & Stack Props`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as dynamodb from "aws-cdk-lib/aws-dynamodb";
import * as lambda from "aws-cdk-lib/aws-lambda";
import * as apigateway from "aws-cdk-lib/aws-apigateway";


interface ServerlessApiStackProps extends cdk.StackProps {
  environment?: "dev" | "prod";
  tableName?: string;
  enableAuth?: boolean;
  enableCors?: boolean;
  readCapacity?: number;
  writeCapacity?: number;
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Imports
- **aws-cdk-lib/core** - Core CDK constructs for defining stacks, apps, and basic infrastructure components
- **constructs** - Base Construct class that all CDK constructs extend from
- **aws-dynamodb** - DynamoDB table constructs for creating NoSQL database tables
- **aws-lambda** - Lambda function constructs for serverless compute resources
- **aws-apigateway** - API Gateway constructs for creating RESTful APIs that trigger Lambda functions
## Props
- **environment** - deployment environment identifier, restricted to "dev" or "prod" for environment-specific configurations
- **tableName** -  custom name for the DynamoDB table, defaults to auto-generated name if not provided
- **enableAuth** -  flag to enable/disable authentication on the API (e.g., API keys, IAM, Cognito)
- **enableCors** -  flag to enable Cross-Origin Resource Sharing for browser-based API access
- **readCapacity** -  provisioned read capacity units for the DynamoDB table (if not using on-demand billing)
- **writeCapacity** -  provisioned write capacity units for the DynamoDB table (if not using on-demand billing)

`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Constructor & dynamodb Table`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
export class ServerlessApiStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props?: ServerlessApiStackProps) {
    super(scope, id, props);

    const environment = props?.environment || "dev";

    const table = new dynamodb.Table(this, "ItemsTable", {
      partitionKey: {name: 'id', type: dynamodb.AttributeType.STRING},
      tableName: props?.tableName,
      billingMode: props?.environment === "prod" ? dynamodb.BillingMode.PROVISIONED : dynamodb.BillingMode.PAY_PER_REQUEST,
      readCapacity: props?.environment === "prod" ? (props.readCapacity || 5) : undefined,
      writeCapacity: props?.environment === 'prod' ? (props.writeCapacity || 5) : undefined,
      removalPolicy: cdk.RemovalPolicy.DESTROY,
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Constructor
- Takes a scope (parent construct), id (unique identifier), and optional props to configure the stack
- Calls super() to initialize the parent cdk.Stack class with the provided parameters
## dynamodb
- **partitionKey** - Defines "id" as the primary key with String data type for item identification
- **tableName** - Uses custom table name from props if provided, otherwise CDK auto-generates a name
- **billingMode** - Uses provisioned capacity for production (predictable costs), pay-per-request for development (scales automatically, no capacity planning)
- **readCapacity** - Sets provisioned read units only in production, defaults to 5 if not specified (undefined for dev/pay-per-request mode)
- **writeCapacity** - Sets provisioned write units only in production, defaults to 5 if not specified (undefined for dev/pay-per-request mode)
- **removalPolicy: DESTROY** - Automatically deletes the table when the stack is destroyed (useful for dev/testing environments)
`}
                    </ReactMarkdown>
                    </div>
<div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Lambdas`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
const createItemFunction = new lambda.Function(this, "CreateItemFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset('lambdas/create-item'),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const getItemsFunction = new lambda.Function(this, "GetItemsFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset("lambdas/get-items"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const updateItemFunction = new lambda.Function(this, "UpdateItemFunctions", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset("lambdas/update-item"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const deleteItemFunction = new lambda.Function(this, "DeleteItemFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.js",
      code: lambda.Code.fromAsset("lambdas/delete-item"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })

    const getItemFunction = new lambda.Function(this, "GetItemFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.js",
      code: lambda.Code.fromAsset("lambdas/get-item"),
      environment: {
        TABLE_NAME: table.tableName,
      }
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Functions
- **createItemFunction** - Handles creating new items in the DynamoDB table
- **getItemsFunction** - Retrieves multiple/all items from the table
- **getItemFunction** - Retrieves a single item by ID
- **updateItemFunction** - Updates existing items in the table
- **deleteItemFunction** - Deletes items from the table
## Configurations
- **runtime** - All functions use Node.js 18.x runtime (can be changed to other runtimes like Python, Java, etc.)
- **handler** - Entry point for the Lambda function, expects "index.handler" or "index.js" in the code directory
- **code** - Loads function code from local directories under "lambdas/" (alternative options include Code.fromInline(), Code.fromBucket(), or Code.fromDockerBuild())
- **environment** - Passes the DynamoDB table name as an environment variable for the function to access
## Customization
- Lambda code must be manually written and placed in the respective directories (e.g., "lambdas/create-item/index.js")
- Handler names and file structures should match your actual implementation
- Runtime can be changed based on your preferred language (Python, Java, Go, etc.)
- Additional configurations like memory size, timeout, layers, and VPC settings can be added as needed
`}
                    </ReactMarkdown>
                    </div>
<div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Lambda Permissions`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    table.grantReadWriteData(createItemFunction);
    table.grantReadData(getItemsFunction)
    table.grantReadWriteData(updateItemFunction);
    table.grantWriteData(deleteItemFunction);
    table.grantReadData(getItemFunction);
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
- These automatically create and attach IAM policies to each Lambda function's execution role
`}
                    </ReactMarkdown>
                    </div>
<div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# API Gateway`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    const api = new apigateway.RestApi(this, "ItemsApi", {
      restApiName: "Items service",
      description: "API for managing items",
      defaultCorsPreflightOptions: props?.enableCors ? {
        allowOrigins: apigateway.Cors.ALL_ORIGINS,
        allowMethods: apigateway.Cors.ALL_METHODS,
      } : undefined,
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## API Gateway Setup
- Creates a REST API named "Items service" for managing items through HTTP endpoints
- **restApiName** - Sets the display name shown in the AWS Console
- **description** - Provides documentation about the API's purpose
- **defaultCorsPreflightOptions** - Conditionally enables CORS based on the enableCors prop
- **allowOrigins: ALL_ORIGINS** - Allows requests from any domain (should be restricted to specific domains in production)
- **allowMethods: ALL_METHODS** - Allows all HTTP methods (GET, POST, PUT, DELETE, etc.)
`}
                    </ReactMarkdown>
                    </div>
<div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# API Resources`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    const items = api.root.addResource("items");

    items.addMethod("POST", new apigateway.LambdaIntegration(createItemFunction));
    items.addMethod("GET", new apigateway.LambdaIntegration(getItemsFunction));
    

    const singleItem = items.addResource("{id}");

    singleItem.addMethod("GET", new apigateway.LambdaIntegration(getItemFunction));
    singleItem.addMethod("PUT", new apigateway.LambdaIntegration(updateItemFunction));
    singleItem.addMethod("DELETE", new apigateway.LambdaIntegration(deleteItemFunction));
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Resources
- **items** - Creates a "/items" resource at the API root for collection-level operations
- **singleItem** - Creates a nested "/{id}" resource under "/items" for individual item operations using a path parameter
## Endpoints
- **POST /items** - Integrated with **createItemFunction** to create new items
- **GET /items** - Integrated with **getItemsFunction** to retrieve all items
- **GET /items/{id}** - Integrated with **getItemFunction** to retrieve a specific item by ID
- **PUT /items/{id}** - Integrated with **updateItemFunction** to update a specific item
- **DELETE /items/{id}** - Integrated with **deleteItemFunction** to delete a specific item
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Outputs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    new cdk.CfnOutput(this, "ApiEndpoint", {
      value: api.url,
      description: "API Gateway endpoint URL",
    })

    new cdk.CfnOutput(this, "TableName", {
      value: table.tableName,
      description: "DynamoDB table name",
    })

    new cdk.CfnOutput(this, "ExampleCurlCOmmand", {
      value: ${`curl -X POST ${"${api.url}"}items -H "Content-type: application/json" -d '{"id":"123", "name": "Test Item"}'`},
      description: "Example command to create an item",
    })

  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Outputs
- These outputs display important values in the terminal after deployment and can be referenced by other stacks or automation tools
`}
                    </ReactMarkdown>
                    </div>
            </main>
        </div>
    )

}
export default ServerlessApi;